import { AUDIO_PROCESSING } from "@/constants/audio";

type ProcessedStreamResult = {
    stream: MediaStream;
    cleanup: () => void;
};

type CallStreamChain = {
    rawStream: MediaStream;
    source: MediaStreamAudioSourceNode;
    destination: MediaStreamAudioDestinationNode;
};

class AudioProcessingService {
    private originalGetUserMedia: typeof navigator.mediaDevices.getUserMedia | null = null;
    private interceptActive: boolean = false;
    private activeCallChains: CallStreamChain[] = [];

    // A single, long-lived AudioContext shared across all calls.
    // Nodes are created/destroyed per-stream; the context itself persists.
    private sharedContext: AudioContext | null = null;

    // Called once on app boot — patches getUserMedia so every subsequent
    // Twilio setInputDevice() call flows through the processing chain.
    applyIntercept(): void {
        if (this.interceptActive) return;

        this.originalGetUserMedia = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
        this.interceptActive = true;

        navigator.mediaDevices.getUserMedia = async (constraints?: MediaStreamConstraints) => {
            const rawStream = await this.originalGetUserMedia!(constraints);

            // Only intercept audio-only streams (Twilio mic requests).
            // Pass through video streams or anything without audio untouched.
            if (!constraints?.audio || rawStream.getVideoTracks().length > 0) {
                return rawStream;
            }

            return this.createCallStream(rawStream);
        };
    }

    removeIntercept(): void {
        if (!this.interceptActive || !this.originalGetUserMedia) return;
        navigator.mediaDevices.getUserMedia = this.originalGetUserMedia;
        this.originalGetUserMedia = null;
        this.interceptActive = false;
    }

    // Must be called SYNCHRONOUSLY at the very start of the call button click handler
    // (before any awaits) so we are still inside the user gesture context.
    // Chrome only allows AudioContext.resume() when triggered by a user gesture.
    prewarm(): void {
        const context = this.getOrCreateContext();
        // Fire-and-forget: we are synchronously inside a user gesture right now,
        // so resume() will succeed immediately in Chrome.
        context.resume().catch(() => undefined);
    }

    // Bypasses the intercept — used by the mic test to get the raw device stream
    // without going through the call processing chain.
    async getOriginalStream(constraints: MediaStreamConstraints): Promise<MediaStream> {
        const gum = this.originalGetUserMedia ?? navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
        return gum(constraints);
    }

    releaseCallStreams(): void {
        const chains = [...this.activeCallChains];
        this.activeCallChains = [];

        for (const chain of chains) {
            chain.destination.stream.getTracks().forEach((track) => {
                if (track.readyState !== "ended") {
                    track.stop();
                }
            });

            chain.rawStream.getTracks().forEach((track) => {
                if (track.readyState !== "ended") {
                    track.stop();
                }
            });

            try {
                chain.source.disconnect();
            } catch {
                // Source may already be disconnected.
            }
        }
    }

    // Public — for the mic visualiser in the settings page.
    // Creates an isolated processing chain completely independent from active calls.
    async buildProcessingChain(rawStream: MediaStream): Promise<ProcessedStreamResult> {
        const context = this.getOrCreateContext();
        await context.resume();

        const destination = this.buildNodes(context, rawStream);

        const cleanup = () => {
            rawStream.getTracks().forEach((t) => t.stop());
        };

        return { stream: destination.destination.stream, cleanup };
    }

    // Private — used exclusively by the getUserMedia intercept (call path).
    // Self-contained: cleanup is bound to track.stop() on the returned stream.
    private async createCallStream(rawStream: MediaStream): Promise<MediaStream> {
        const context = this.getOrCreateContext();

        // Context should already be running (prewarm was called on the call button click).
        // Resume as a safety net for device-switch mid-call (always triggered by a click).
        await context.resume();

        const { destination, source } = this.buildNodes(context, rawStream);
        const chain: CallStreamChain = { rawStream, source, destination };
        this.activeCallChains.push(chain);

        // Tie raw stream cleanup to the processed track's stop event.
        destination.stream.getTracks().forEach((track) => {
            const originalStop = track.stop.bind(track);
            track.stop = () => {
                this.removeCallChain(chain);
                rawStream.getTracks().forEach((t) => t.stop());
                originalStop();
            };
        });

        return destination.stream;
    }

    private removeCallChain(chain: CallStreamChain): void {
        this.activeCallChains = this.activeCallChains.filter((activeChain) => activeChain !== chain);

        try {
            chain.source.disconnect();
        } catch {
            // Source may already be disconnected.
        }
    }

    private getOrCreateContext(): AudioContext {
        if (!this.sharedContext || this.sharedContext.state === "closed") {
            this.sharedContext = new AudioContext({ sampleRate: AUDIO_PROCESSING.SAMPLE_RATE });
        }
        return this.sharedContext;
    }

    // Builds and connects all processing nodes on the given context.
    private buildNodes(
        context: AudioContext,
        rawStream: MediaStream
    ): { destination: MediaStreamAudioDestinationNode; source: MediaStreamAudioSourceNode } {
        const source = context.createMediaStreamSource(rawStream);

        // 1. High-pass filter — cut everything below 80 Hz (rumble, desk vibrations)
        const hpf = context.createBiquadFilter();
        hpf.type = "highpass";
        hpf.frequency.value = AUDIO_PROCESSING.HPF_FREQUENCY;
        hpf.Q.value = AUDIO_PROCESSING.HPF_Q;

        // 2. Low-mid cut — tame the boxy 500 Hz resonance common in small capsule mics
        const lowMidCut = context.createBiquadFilter();
        lowMidCut.type = "peaking";
        lowMidCut.frequency.value = AUDIO_PROCESSING.LOW_MID_CUT_FREQUENCY;
        lowMidCut.gain.value = AUDIO_PROCESSING.LOW_MID_CUT_GAIN;
        lowMidCut.Q.value = AUDIO_PROCESSING.LOW_MID_CUT_Q;

        // 3. Lower presence boost — 2.5 kHz adds warmth and fundamental speech clarity
        const lowerPresence = context.createBiquadFilter();
        lowerPresence.type = "peaking";
        lowerPresence.frequency.value = AUDIO_PROCESSING.LOWER_PRESENCE_FREQUENCY;
        lowerPresence.gain.value = AUDIO_PROCESSING.LOWER_PRESENCE_GAIN;
        lowerPresence.Q.value = AUDIO_PROCESSING.LOWER_PRESENCE_Q;

        // 4. Upper presence boost — 5 kHz sharpens consonants (t, d, k, p) and overall crispness
        const upperPresence = context.createBiquadFilter();
        upperPresence.type = "peaking";
        upperPresence.frequency.value = AUDIO_PROCESSING.UPPER_PRESENCE_FREQUENCY;
        upperPresence.gain.value = AUDIO_PROCESSING.UPPER_PRESENCE_GAIN;
        upperPresence.Q.value = AUDIO_PROCESSING.UPPER_PRESENCE_Q;

        // 5. Air shelf — open high-end above 10 kHz (s, sh, ch sibilants)
        const airShelf = context.createBiquadFilter();
        airShelf.type = "highshelf";
        airShelf.frequency.value = AUDIO_PROCESSING.AIR_FREQUENCY;
        airShelf.gain.value = AUDIO_PROCESSING.AIR_GAIN;

        // 6. Dynamics compressor — transparent levelling, replaces browser AGC
        const compressor = context.createDynamicsCompressor();
        compressor.threshold.value = AUDIO_PROCESSING.COMPRESSOR_THRESHOLD;
        compressor.knee.value = AUDIO_PROCESSING.COMPRESSOR_KNEE;
        compressor.ratio.value = AUDIO_PROCESSING.COMPRESSOR_RATIO;
        compressor.attack.value = AUDIO_PROCESSING.COMPRESSOR_ATTACK;
        compressor.release.value = AUDIO_PROCESSING.COMPRESSOR_RELEASE;

        // 7. Output gain — slight level boost after gentler compression
        const outputGain = context.createGain();
        outputGain.gain.value = AUDIO_PROCESSING.OUTPUT_GAIN;

        const destination = context.createMediaStreamDestination();

        source
            .connect(hpf)
            .connect(lowMidCut)
            .connect(lowerPresence)
            .connect(upperPresence)
            .connect(airShelf)
            .connect(compressor)
            .connect(outputGain)
            .connect(destination);

        return { destination, source };
    }
}

export const audioProcessingService = new AudioProcessingService();
